/**
 * nav.ts — The app-shell navigation model.
 *
 * Mirrors the CME Studio prototype's `shell.jsx` Sidebar `sections` array
 * (cme/project/app, bundle 8g2bTVkJuAkJdrsriC0NeQ). Each stage maps to a
 * route; the sidebar, breadcrumb, and AI-assistant context all derive from
 * this single source of truth.
 */
import { I, type IconComponent } from "../components/icons";
import { type TenderProjectStatus } from "./tenderApi";

export type StageId = "ingest" | "audit" | "strategy" | "kb" | "kb-explore" | "suppliers";

export type SectionLabel = "PROGETTO" | "ARCHIVIO";

export interface NavItem {
  id: StageId;
  /** Sidebar label (Italian, matching the prototype copy). */
  label: string;
  /** Router path. */
  path: string;
  icon: IconComponent;
  section: SectionLabel;
  /** AI-assistant context key (drives suggested prompts + the context label). */
  aiContext: string;
  /** When true, the right-rail AI assistant is hidden on this stage
   *  (Esplora KB has its own integrated chat panel — see CD-4/CD-5). */
  hideAI?: boolean;
  /** Section gated behind an "In arrivo" screen (no backend domain yet). */
  comingSoon?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  {
    id: "ingest",
    label: "Workspace",
    path: "/",
    icon: I.Upload,
    section: "PROGETTO",
    aiContext: "ingest",
    // `P-66` — Omar, 2026-09-11: "There must be no AI assistant on Workspace,
    // Knowledge Base and Fornitori & Storico." The rail is mounted once in
    // `AppShell` for every stage that does not set this, so the flag IS the
    // removal. Nothing can be asked on these screens today either -- the live
    // chat needs an active project and only Audit and Strategia publish one --
    // but a tab labelled "Assistente AI" is still an assistant on the screen.
    hideAI: true,
  },
  {
    id: "audit",
    label: "Audit & Discrepanze",
    path: "/audit",
    icon: I.Audit,
    section: "PROGETTO",
    aiContext: "audit",
  },
  {
    id: "strategy",
    label: "Strategia & Margini",
    path: "/strategy",
    icon: I.Strategy,
    section: "PROGETTO",
    aiContext: "strategy",
  },
  {
    id: "kb",
    label: "Knowledge Base",
    path: "/kb",
    icon: I.Layers,
    section: "ARCHIVIO",
    aiContext: "kb",
    // `P-66` — Omar, 2026-09-11: "There must be no AI assistant on Workspace,
    // Knowledge Base and Fornitori & Storico." The rail is mounted once in
    // `AppShell` for every stage that does not set this, so the flag IS the
    // removal. Nothing can be asked on these screens today either -- the live
    // chat needs an active project and only Audit and Strategia publish one --
    // but a tab labelled "Assistente AI" is still an assistant on the screen.
    hideAI: true,
  },
  {
    id: "kb-explore",
    label: "Esplora KB",
    path: "/kb/explore",
    icon: I.Network,
    section: "ARCHIVIO",
    aiContext: "kb",
    hideAI: true,
  },
  {
    id: "suppliers",
    label: "Fornitori & Storico",
    path: "/suppliers",
    icon: I.Suppliers,
    section: "PROGETTO",
    aiContext: "suppliers",
    // `P-66` — Omar, 2026-09-11: "There must be no AI assistant on Workspace,
    // Knowledge Base and Fornitori & Storico." The rail is mounted once in
    // `AppShell` for every stage that does not set this, so the flag IS the
    // removal. Nothing can be asked on these screens today either -- the live
    // chat needs an active project and only Audit and Strategia publish one --
    // but a tab labelled "Assistente AI" is still an assistant on the screen.
    hideAI: true,
  },
];

export const SECTIONS: { label: SectionLabel; items: NavItem[] }[] = [
  {
    label: "PROGETTO",
    items: NAV_ITEMS.filter((i) => i.section === "PROGETTO" && i.id !== "suppliers"),
  },
  {
    label: "ARCHIVIO",
    items: [
      ...NAV_ITEMS.filter((i) => i.id === "kb" || i.id === "kb-explore"),
      ...NAV_ITEMS.filter((i) => i.id === "suppliers"),
    ],
  },
];

/** Resolve the active nav item from a router pathname. */
export function stageFromPath(pathname: string): NavItem {
  // Most specific first ("/kb/explore" before "/kb"); "/" matches exactly.
  const byLength = [...NAV_ITEMS].sort((a, b) => b.path.length - a.path.length);
  const match = byLength.find((i) =>
    i.path === "/" ? pathname === "/" : pathname.startsWith(i.path),
  );
  return match ?? NAV_ITEMS[0];
}

/** Italian label for the AI-assistant context line. */
export function contextLabel(context: string): string {
  return (
    {
      ingest: "Workspace",
      audit: "Audit",
      strategy: "Strategia",
      suppliers: "Fornitori",
      kb: "Knowledge Base",
    }[context] ?? "Progetto"
  );
}

/**
 * `P-63` — where opening a project lands you, and the ONE definition of it.
 *
 * Omar, 2026-09-02: *"If a project has not yet been analysed, opening it must
 * take the user to the Passo 1 / Passo 2 preparation screen… This rule should
 * apply regardless of how the user reaches the project: «Apri progetto»,
 * project switcher, sidebar, bookmark, or any future entry point."*
 *
 * Exported as a FUNCTION, not as a status constant each caller re-tests.
 * `feedback_one_predicate_not_two_callers`: two matchers sharing a constant are
 * still two matchers, and the second one is where the rule quietly stops being
 * true. Five call sites use this — the dashboard's «Apri progetto» dropdown,
 * the dashboard row (which is also the three-dot «Apri»), the sidebar's project
 * switcher, the sidebar's Audit item, and the AuditPage's own redirect.
 *
 * The status is the SERVER's answer, not a prediction we maintain: a project
 * leaves `ingest` only when an analysis has actually completed
 * (`analysis/runner.py:170` and `:592` are the only two writers, both on the
 * success path). So `ingest` means exactly *prepared · running · failed* —
 * the three states the preparation screen is built to render — and everything
 * else means there is an analysis to look at.
 *
 * `archived` therefore routes to the audit. That is deliberate and it is also
 * unreachable through any door in the product: archiving OVERWRITES the status
 * (`api/projects.py:312`), losing whether the project had been analysed, and
 * the project list filters archived rows out (`:157`).
 */
export function projectHasAnalysis(status: TenderProjectStatus): boolean {
  return status !== "ingest";
}

/** The path that `projectHasAnalysis` chooses. The only place either URL is built. */
export function projectEntryPath(project: { id: string; status: TenderProjectStatus }): string {
  return projectHasAnalysis(project.status) ? `/audit/${project.id}` : preparePath(project.id);
}

/** The preparation wizard's address. `P-63` gave it one; before that it had none. */
export function preparePath(projectId: string): string {
  return `/projects/${projectId}/prepare`;
}

/**
 * Strategia & Margini for one project. `P-65` — before this existed the route
 * carried NO project id, so the page fell through to `projects[0]`, the
 * most-recently-updated project, and clicking «Strategia & Margini» from
 * AOSTA's Audit screen opened Torino's margins. Audit had already been given an
 * id for exactly this reason; strategy was left behind.
 *
 * Unlike `projectEntryPath` this does NOT branch on whether the project has an
 * analysis. Sending an un-analysed project somewhere else would reintroduce the
 * defect in a quieter form — the user asked for THIS project's margins, and the
 * page's own empty state is the honest answer when there are none.
 */
export function strategyPath(projectId: string): string {
  return `/strategy/${projectId}`;
}
